import { CdkListboxModule } from '@angular/cdk/listbox';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../../base/base.component';
import { CheckboxComponent } from '../../../core/controls/checkbox.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { FieldContainerComponent } from '../../../core/fields/field-container.component';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { ValidatorsEx } from '../../../../validators/validators-ex';

@Component({
  selector: 'app-financial-criteria',
  imports: [
    RadioButtonComponent,
    SelectComponent,
    InputComponent,
    CheckboxComponent,
    CommonModule,
    CdkListboxModule,
    FieldContainerComponent,
  ],
  template: `
    <ng-template #financialTemplate let-item>
      <app-field-container
        [label]="item.label"
        [labelClasses]="item.groupHead ? 'tw-text-[15px]' : 'tw-pl-2'"
        [bold]="item.groupHead"
      >
        <ng-template #value>
          <div
            class="tw-flex tw-flex-col md:tw-flex-row tw-flex-full tw-items-start"
          >
            <app-select
              [control]="item.controls.operator"
              optionLabel="description"
              optionValue="value"
              label="Choose One"
              [virtualScroll]="false"
              [showClear]="false"
              [options]="options"
              class="tw-pt-1 tw-w-full md:tw-w-[220px]"
              [showHelpText]="false"
            ></app-select>
            <div
              class="tw-flex tw-flex-row tw-pt-1 tw-w-full md:tw-w-auto md:tw-min-w-[250px] md:tw-ml-2"
            >
              <app-input
                type="Currency"
                [control]="item.controls.rangeOne"
                [class]="
                  item.controls.operator.value === 'between'
                    ? 'tw-w-1/2 md:tw-w-[125px]'
                    : 'tw-w-full md:tw-w-[250px]'
                "
                [showHelpText]="true"
                [screenReaderLabel]="item.label + ' from amount'"
                label=""
              />
              @if (item.controls.operator.value === 'between') {
                <app-input
                  type="Currency"
                  [control]="item.controls.rangeTwo"
                  class="tw-w-1/2 md:tw-w-[125px]"
                  [showHelpText]="true"
                  [screenReaderLabel]="item.label + ' to amount'"
                  label=""
                />
              } @else {}
            </div>
            <app-checkbox
              [control]="item.controls.excludeZero"
              [showHelpText]="false"
              label="Exclude zero valued claims"
              class="tw-pl-2 tw-min-w-[240px] tw-pt-[8px]"
              [showHelpText]="false"
            />
          </div>
        </ng-template>
      </app-field-container>
    </ng-template>
    <div class="tw-pt-2 tw-pl-4 tw-flex tw-flex-col tw-gap-4 tw-w-full">
      <app-radio-button
        [control]="selectMode"
        screenReaderLabel="Selection mode"
        optionLabel="name"
        optionValue="code"
        [options]="selectModes"
        [showHelpText]="false"
        [vertical]="false"
      ></app-radio-button>
      <h4
        class="tw-font-bold tw-text-lg tw-py-2 tw-border-b tw-bg-[var(--sed-grey-40-colorx)] tw-m-0"
      >
        Claim financial filters
      </h4>
      @for (
        financialCriteria of claimFinancialCriterias;
        track financialCriteria
      ) {
        <ng-container
          *ngTemplateOutlet="
            financialTemplate;
            context: { $implicit: financialCriteria }
          "
        ></ng-container>
      }
      <h4
        class="tw-font-bold tw-text-lg tw-py-2 tw-border-b tw-bg-[var(--sed-grey-40-colorx)] tw-m-0"
      >
        Event financial filters
      </h4>
      @for (
        financialCriteria of eventFinancialCriterias;
        track financialCriteria
      ) {
        <ng-container
          *ngTemplateOutlet="
            financialTemplate;
            context: { $implicit: financialCriteria }
          "
        ></ng-container>
      }
    </div>
  `,
  styles: [
    `
      :host ::ng-deep app-field-container > div > div:first-child {
        flex-basis: 100% !important;
      }
      :host ::ng-deep app-field-container > div > div:nth-child(2) {
        flex-basis: 100% !important;
      }

      @media (min-width: 768px) {
        :host ::ng-deep app-field-container > div > div:first-child {
          flex-basis: 130px !important;
          flex-shrink: 0 !important;
          flex-grow: 0 !important;
        }
        :host ::ng-deep app-field-container > div > div:nth-child(2) {
          flex-basis: 0 !important;
          flex-grow: 1 !important;
          min-width: 0 !important;
        }
        :host ::ng-deep app-select,
        :host ::ng-deep app-input {
          display: block;
          min-width: 0;
        }
        :host ::ng-deep .p-select-label > .option-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    `,
  ],
})
export class FinancialCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  @Input({ required: true }) section: FilterSection;

  selectMode = new FormControl<string>('gross');

  options = [
    { description: 'Between', value: 'between' },
    { description: 'Equal', value: 'equal' },
    { description: 'Greater than', value: 'greater_than' },
    { description: 'Greater than equal to', value: 'greater_than_equal_to' },
    { description: 'Less than', value: 'less_than' },
    { description: 'Less than equal to', value: 'less_than_equal_to' },
  ];

  selectModes = [
    { name: 'Gross', code: 'gross' },
    { name: 'Net', code: 'net' },
  ];

  fg = new FormGroup({
    // Claim Incurred (4 fields)
    claimIncurredTotalOperator: new FormControl<string>(''),
    claimIncurredTotalRangeOne: new FormControl<number | null>(null),
    claimIncurredTotalRangeTwo: new FormControl<number | null>(null),
    claimIncurredTotalExcludeZero: new FormControl<boolean>(false),
    claimIncurredIndemnityOperator: new FormControl<string>(''),
    claimIncurredIndemnityRangeOne: new FormControl<number | null>(null),
    claimIncurredIndemnityRangeTwo: new FormControl<number | null>(null),
    claimIncurredIndemnityExcludeZero: new FormControl<boolean>(false),
    claimIncurredMedicalOperator: new FormControl<string>(''),
    claimIncurredMedicalRangeOne: new FormControl<number | null>(null),
    claimIncurredMedicalRangeTwo: new FormControl<number | null>(null),
    claimIncurredMedicalExcludeZero: new FormControl<boolean>(false),
    claimIncurredExpenseOperator: new FormControl<string>(''),
    claimIncurredExpenseRangeOne: new FormControl<number | null>(null),
    claimIncurredExpenseRangeTwo: new FormControl<number | null>(null),
    claimIncurredExpenseExcludeZero: new FormControl<boolean>(false),
    // Claim Outstanding (4 fields)
    claimOutstandingTotalOperator: new FormControl<string>(''),
    claimOutstandingTotalRangeOne: new FormControl<number | null>(null),
    claimOutstandingTotalRangeTwo: new FormControl<number | null>(null),
    claimOutstandingTotalExcludeZero: new FormControl<boolean>(false),
    claimOutstandingIndemnityOperator: new FormControl<string>(''),
    claimOutstandingIndemnityRangeOne: new FormControl<number | null>(null),
    claimOutstandingIndemnityRangeTwo: new FormControl<number | null>(null),
    claimOutstandingIndemnityExcludeZero: new FormControl<boolean>(false),
    claimOutstandingMedicalOperator: new FormControl<string>(''),
    claimOutstandingMedicalRangeOne: new FormControl<number | null>(null),
    claimOutstandingMedicalRangeTwo: new FormControl<number | null>(null),
    claimOutstandingMedicalExcludeZero: new FormControl<boolean>(false),
    claimOutstandingExpenseOperator: new FormControl<string>(''),
    claimOutstandingExpenseRangeOne: new FormControl<number | null>(null),
    claimOutstandingExpenseRangeTwo: new FormControl<number | null>(null),
    claimOutstandingExpenseExcludeZero: new FormControl<boolean>(false),
    // Claim Paid (4 fields)
    claimPaidTotalOperator: new FormControl<string>(''),
    claimPaidTotalRangeOne: new FormControl<number | null>(null),
    claimPaidTotalRangeTwo: new FormControl<number | null>(null),
    claimPaidTotalExcludeZero: new FormControl<boolean>(false),
    claimPaidIndemnityOperator: new FormControl<string>(''),
    claimPaidIndemnityRangeOne: new FormControl<number | null>(null),
    claimPaidIndemnityRangeTwo: new FormControl<number | null>(null),
    claimPaidIndemnityExcludeZero: new FormControl<boolean>(false),
    claimPaidMedicalOperator: new FormControl<string>(''),
    claimPaidMedicalRangeOne: new FormControl<number | null>(null),
    claimPaidMedicalRangeTwo: new FormControl<number | null>(null),
    claimPaidMedicalExcludeZero: new FormControl<boolean>(false),
    claimPaidExpenseOperator: new FormControl<string>(''),
    claimPaidExpenseRangeOne: new FormControl<number | null>(null),
    claimPaidExpenseRangeTwo: new FormControl<number | null>(null),
    claimPaidExpenseExcludeZero: new FormControl<boolean>(false),
    // Claim Recovered (4 fields)
    claimRecoveredTotalOperator: new FormControl<string>(''),
    claimRecoveredTotalRangeOne: new FormControl<number | null>(null),
    claimRecoveredTotalRangeTwo: new FormControl<number | null>(null),
    claimRecoveredTotalExcludeZero: new FormControl<boolean>(false),
    claimRecoveredIndemnityOperator: new FormControl<string>(''),
    claimRecoveredIndemnityRangeOne: new FormControl<number | null>(null),
    claimRecoveredIndemnityRangeTwo: new FormControl<number | null>(null),
    claimRecoveredIndemnityExcludeZero: new FormControl<boolean>(false),
    claimRecoveredMedicalOperator: new FormControl<string>(''),
    claimRecoveredMedicalRangeOne: new FormControl<number | null>(null),
    claimRecoveredMedicalRangeTwo: new FormControl<number | null>(null),
    claimRecoveredMedicalExcludeZero: new FormControl<boolean>(false),
    claimRecoveredExpenseOperator: new FormControl<string>(''),
    claimRecoveredExpenseRangeOne: new FormControl<number | null>(null),
    claimRecoveredExpenseRangeTwo: new FormControl<number | null>(null),
    claimRecoveredExpenseExcludeZero: new FormControl<boolean>(false),
    // Event Incurred (4 fields)
    eventIncurredTotalOperator: new FormControl<string>(''),
    eventIncurredTotalRangeOne: new FormControl<number | null>(null),
    eventIncurredTotalRangeTwo: new FormControl<number | null>(null),
    eventIncurredTotalExcludeZero: new FormControl<boolean>(false),
    eventIncurredIndemnityOperator: new FormControl<string>(''),
    eventIncurredIndemnityRangeOne: new FormControl<number | null>(null),
    eventIncurredIndemnityRangeTwo: new FormControl<number | null>(null),
    eventIncurredIndemnityExcludeZero: new FormControl<boolean>(false),
    eventIncurredMedicalOperator: new FormControl<string>(''),
    eventIncurredMedicalRangeOne: new FormControl<number | null>(null),
    eventIncurredMedicalRangeTwo: new FormControl<number | null>(null),
    eventIncurredMedicalExcludeZero: new FormControl<boolean>(false),
    eventIncurredExpenseOperator: new FormControl<string>(''),
    eventIncurredExpenseRangeOne: new FormControl<number | null>(null),
    eventIncurredExpenseRangeTwo: new FormControl<number | null>(null),
    eventIncurredExpenseExcludeZero: new FormControl<boolean>(false),
    // Event Outstanding (4 fields)
    eventOutstandingTotalOperator: new FormControl<string>(''),
    eventOutstandingTotalRangeOne: new FormControl<number | null>(null),
    eventOutstandingTotalRangeTwo: new FormControl<number | null>(null),
    eventOutstandingTotalExcludeZero: new FormControl<boolean>(false),
    eventOutstandingIndemnityOperator: new FormControl<string>(''),
    eventOutstandingIndemnityRangeOne: new FormControl<number | null>(null),
    eventOutstandingIndemnityRangeTwo: new FormControl<number | null>(null),
    eventOutstandingIndemnityExcludeZero: new FormControl<boolean>(false),
    eventOutstandingMedicalOperator: new FormControl<string>(''),
    eventOutstandingMedicalRangeOne: new FormControl<number | null>(null),
    eventOutstandingMedicalRangeTwo: new FormControl<number | null>(null),
    eventOutstandingMedicalExcludeZero: new FormControl<boolean>(false),
    eventOutstandingExpenseOperator: new FormControl<string>(''),
    eventOutstandingExpenseRangeOne: new FormControl<number | null>(null),
    eventOutstandingExpenseRangeTwo: new FormControl<number | null>(null),
    eventOutstandingExpenseExcludeZero: new FormControl<boolean>(false),
    // Event Paid (4 fields)
    eventPaidTotalOperator: new FormControl<string>(''),
    eventPaidTotalRangeOne: new FormControl<number | null>(null),
    eventPaidTotalRangeTwo: new FormControl<number | null>(null),
    eventPaidTotalExcludeZero: new FormControl<boolean>(false),
    eventPaidIndemnityOperator: new FormControl<string>(''),
    eventPaidIndemnityRangeOne: new FormControl<number | null>(null),
    eventPaidIndemnityRangeTwo: new FormControl<number | null>(null),
    eventPaidIndemnityExcludeZero: new FormControl<boolean>(false),
    eventPaidMedicalOperator: new FormControl<string>(''),
    eventPaidMedicalRangeOne: new FormControl<number | null>(null),
    eventPaidMedicalRangeTwo: new FormControl<number | null>(null),
    eventPaidMedicalExcludeZero: new FormControl<boolean>(false),
    eventPaidExpenseOperator: new FormControl<string>(''),
    eventPaidExpenseRangeOne: new FormControl<number | null>(null),
    eventPaidExpenseRangeTwo: new FormControl<number | null>(null),
    eventPaidExpenseExcludeZero: new FormControl<boolean>(false),
    // Event Recovered (4 fields)
    eventRecoveredTotalOperator: new FormControl<string>(''),
    eventRecoveredTotalRangeOne: new FormControl<number | null>(null),
    eventRecoveredTotalRangeTwo: new FormControl<number | null>(null),
    eventRecoveredTotalExcludeZero: new FormControl<boolean>(false),
    eventRecoveredIndemnityOperator: new FormControl<string>(''),
    eventRecoveredIndemnityRangeOne: new FormControl<number | null>(null),
    eventRecoveredIndemnityRangeTwo: new FormControl<number | null>(null),
    eventRecoveredIndemnityExcludeZero: new FormControl<boolean>(false),
    eventRecoveredMedicalOperator: new FormControl<string>(''),
    eventRecoveredMedicalRangeOne: new FormControl<number | null>(null),
    eventRecoveredMedicalRangeTwo: new FormControl<number | null>(null),
    eventRecoveredMedicalExcludeZero: new FormControl<boolean>(false),
    eventRecoveredExpenseOperator: new FormControl<string>(''),
    eventRecoveredExpenseRangeOne: new FormControl<number | null>(null),
    eventRecoveredExpenseRangeTwo: new FormControl<number | null>(null),
    eventRecoveredExpenseExcludeZero: new FormControl<boolean>(false),
  });

  claimFinancialCriterias: FinancialCriteria[] = [
    // Incurred bucket
    {
      label: 'Incurred',
      groupHead: true,
      column: 'INCURRED_TOTAL',
      tableAlias: 'claim_incurred_as_of',
      controls: {
        operator: this.fg.controls.claimIncurredTotalOperator,
        rangeOne: this.fg.controls.claimIncurredTotalRangeOne,
        rangeTwo: this.fg.controls.claimIncurredTotalRangeTwo,
        excludeZero: this.fg.controls.claimIncurredTotalExcludeZero,
      },
    },
    {
      label: 'Indemnity',
      groupHead: false,
      column: 'INCURRED_LOSS',
      tableAlias: 'claim_incurred_as_of',
      controls: {
        operator: this.fg.controls.claimIncurredIndemnityOperator,
        rangeOne: this.fg.controls.claimIncurredIndemnityRangeOne,
        rangeTwo: this.fg.controls.claimIncurredIndemnityRangeTwo,
        excludeZero: this.fg.controls.claimIncurredIndemnityExcludeZero,
      },
    },
    {
      label: 'Medical',
      groupHead: false,
      column: 'INCURRED_MEDICAL',
      tableAlias: 'claim_incurred_as_of',
      controls: {
        operator: this.fg.controls.claimIncurredMedicalOperator,
        rangeOne: this.fg.controls.claimIncurredMedicalRangeOne,
        rangeTwo: this.fg.controls.claimIncurredMedicalRangeTwo,
        excludeZero: this.fg.controls.claimIncurredMedicalExcludeZero,
      },
    },
    {
      label: 'Expense',
      groupHead: false,
      column: 'INCURRED_EXPENSE',
      tableAlias: 'claim_incurred_as_of',
      controls: {
        operator: this.fg.controls.claimIncurredExpenseOperator,
        rangeOne: this.fg.controls.claimIncurredExpenseRangeOne,
        rangeTwo: this.fg.controls.claimIncurredExpenseRangeTwo,
        excludeZero: this.fg.controls.claimIncurredExpenseExcludeZero,
      },
    },
    // Outstanding (Future Reserves) bucket
    {
      label: 'Future reserves',
      groupHead: true,
      column: 'OUTSTANDING_TOTAL',
      tableAlias: 'claim_incurred_as_of',
      controls: {
        operator: this.fg.controls.claimOutstandingTotalOperator,
        rangeOne: this.fg.controls.claimOutstandingTotalRangeOne,
        rangeTwo: this.fg.controls.claimOutstandingTotalRangeTwo,
        excludeZero: this.fg.controls.claimOutstandingTotalExcludeZero,
      },
    },
    {
      label: 'Indemnity',
      groupHead: false,
      column: 'OUTSTANDING_LOSS',
      tableAlias: 'claim_incurred_as_of',
      controls: {
        operator: this.fg.controls.claimOutstandingIndemnityOperator,
        rangeOne: this.fg.controls.claimOutstandingIndemnityRangeOne,
        rangeTwo: this.fg.controls.claimOutstandingIndemnityRangeTwo,
        excludeZero: this.fg.controls.claimOutstandingIndemnityExcludeZero,
      },
    },
    {
      label: 'Medical',
      groupHead: false,
      column: 'OUTSTANDING_MEDICAL',
      tableAlias: 'claim_incurred_as_of',
      controls: {
        operator: this.fg.controls.claimOutstandingMedicalOperator,
        rangeOne: this.fg.controls.claimOutstandingMedicalRangeOne,
        rangeTwo: this.fg.controls.claimOutstandingMedicalRangeTwo,
        excludeZero: this.fg.controls.claimOutstandingMedicalExcludeZero,
      },
    },
    {
      label: 'Expense',
      groupHead: false,
      column: 'OUTSTANDING_EXPENSE',
      tableAlias: 'claim_incurred_as_of',
      controls: {
        operator: this.fg.controls.claimOutstandingExpenseOperator,
        rangeOne: this.fg.controls.claimOutstandingExpenseRangeOne,
        rangeTwo: this.fg.controls.claimOutstandingExpenseRangeTwo,
        excludeZero: this.fg.controls.claimOutstandingExpenseExcludeZero,
      },
    },
    // Paid bucket
    {
      label: 'Paid',
      groupHead: true,
      column: 'PAID_TOTAL',
      tableAlias: 'claim_paid_as_of',
      controls: {
        operator: this.fg.controls.claimPaidTotalOperator,
        rangeOne: this.fg.controls.claimPaidTotalRangeOne,
        rangeTwo: this.fg.controls.claimPaidTotalRangeTwo,
        excludeZero: this.fg.controls.claimPaidTotalExcludeZero,
      },
    },
    {
      label: 'Indemnity',
      groupHead: false,
      column: 'PAID_LOSS',
      tableAlias: 'claim_paid_as_of',
      controls: {
        operator: this.fg.controls.claimPaidIndemnityOperator,
        rangeOne: this.fg.controls.claimPaidIndemnityRangeOne,
        rangeTwo: this.fg.controls.claimPaidIndemnityRangeTwo,
        excludeZero: this.fg.controls.claimPaidIndemnityExcludeZero,
      },
    },
    {
      label: 'Medical',
      groupHead: false,
      column: 'PAID_MEDICAL',
      tableAlias: 'claim_paid_as_of',
      controls: {
        operator: this.fg.controls.claimPaidMedicalOperator,
        rangeOne: this.fg.controls.claimPaidMedicalRangeOne,
        rangeTwo: this.fg.controls.claimPaidMedicalRangeTwo,
        excludeZero: this.fg.controls.claimPaidMedicalExcludeZero,
      },
    },
    {
      label: 'Expense',
      groupHead: false,
      column: 'PAID_EXPENSE',
      tableAlias: 'claim_paid_as_of',
      controls: {
        operator: this.fg.controls.claimPaidExpenseOperator,
        rangeOne: this.fg.controls.claimPaidExpenseRangeOne,
        rangeTwo: this.fg.controls.claimPaidExpenseRangeTwo,
        excludeZero: this.fg.controls.claimPaidExpenseExcludeZero,
      },
    },
    // Recovered bucket
    {
      label: 'Recoveries',
      groupHead: true,
      column: 'RECOVERED_TOTAL',
      tableAlias: 'claim_paid_as_of',
      controls: {
        operator: this.fg.controls.claimRecoveredTotalOperator,
        rangeOne: this.fg.controls.claimRecoveredTotalRangeOne,
        rangeTwo: this.fg.controls.claimRecoveredTotalRangeTwo,
        excludeZero: this.fg.controls.claimRecoveredTotalExcludeZero,
      },
    },
    {
      label: 'Indemnity',
      groupHead: false,
      column: 'RECOVERED_LOSS',
      tableAlias: 'claim_paid_as_of',
      controls: {
        operator: this.fg.controls.claimRecoveredIndemnityOperator,
        rangeOne: this.fg.controls.claimRecoveredIndemnityRangeOne,
        rangeTwo: this.fg.controls.claimRecoveredIndemnityRangeTwo,
        excludeZero: this.fg.controls.claimRecoveredIndemnityExcludeZero,
      },
    },
    {
      label: 'Medical',
      groupHead: false,
      column: 'RECOVERED_MEDICAL',
      tableAlias: 'claim_paid_as_of',
      controls: {
        operator: this.fg.controls.claimRecoveredMedicalOperator,
        rangeOne: this.fg.controls.claimRecoveredMedicalRangeOne,
        rangeTwo: this.fg.controls.claimRecoveredMedicalRangeTwo,
        excludeZero: this.fg.controls.claimRecoveredMedicalExcludeZero,
      },
    },
    {
      label: 'Expense',
      groupHead: false,
      column: 'RECOVERED_EXPENSE',
      tableAlias: 'claim_paid_as_of',
      controls: {
        operator: this.fg.controls.claimRecoveredExpenseOperator,
        rangeOne: this.fg.controls.claimRecoveredExpenseRangeOne,
        rangeTwo: this.fg.controls.claimRecoveredExpenseRangeTwo,
        excludeZero: this.fg.controls.claimRecoveredExpenseExcludeZero,
      },
    },
  ];

  eventFinancialCriterias: FinancialCriteria[] = [
    // Incurred bucket
    {
      label: 'Incurred',
      groupHead: true,
      column: 'INCURRED_TOTAL',
      tableAlias: 'event_incurred_as_of',
      controls: {
        operator: this.fg.controls.eventIncurredTotalOperator,
        rangeOne: this.fg.controls.eventIncurredTotalRangeOne,
        rangeTwo: this.fg.controls.eventIncurredTotalRangeTwo,
        excludeZero: this.fg.controls.eventIncurredTotalExcludeZero,
      },
    },
    {
      label: 'Indemnity',
      groupHead: false,
      column: 'INCURRED_LOSS',
      tableAlias: 'event_incurred_as_of',
      controls: {
        operator: this.fg.controls.eventIncurredIndemnityOperator,
        rangeOne: this.fg.controls.eventIncurredIndemnityRangeOne,
        rangeTwo: this.fg.controls.eventIncurredIndemnityRangeTwo,
        excludeZero: this.fg.controls.eventIncurredIndemnityExcludeZero,
      },
    },
    {
      label: 'Medical',
      groupHead: false,
      column: 'INCURRED_MEDICAL',
      tableAlias: 'event_incurred_as_of',
      controls: {
        operator: this.fg.controls.eventIncurredMedicalOperator,
        rangeOne: this.fg.controls.eventIncurredMedicalRangeOne,
        rangeTwo: this.fg.controls.eventIncurredMedicalRangeTwo,
        excludeZero: this.fg.controls.eventIncurredMedicalExcludeZero,
      },
    },
    {
      label: 'Expense',
      groupHead: false,
      column: 'INCURRED_EXPENSE',
      tableAlias: 'event_incurred_as_of',
      controls: {
        operator: this.fg.controls.eventIncurredExpenseOperator,
        rangeOne: this.fg.controls.eventIncurredExpenseRangeOne,
        rangeTwo: this.fg.controls.eventIncurredExpenseRangeTwo,
        excludeZero: this.fg.controls.eventIncurredExpenseExcludeZero,
      },
    },
    // Outstanding (Future Reserves) bucket
    {
      label: 'Future reserves',
      groupHead: true,
      column: 'OUTSTANDING_TOTAL',
      tableAlias: 'event_incurred_as_of',
      controls: {
        operator: this.fg.controls.eventOutstandingTotalOperator,
        rangeOne: this.fg.controls.eventOutstandingTotalRangeOne,
        rangeTwo: this.fg.controls.eventOutstandingTotalRangeTwo,
        excludeZero: this.fg.controls.eventOutstandingTotalExcludeZero,
      },
    },
    {
      label: 'Indemnity',
      groupHead: false,
      column: 'OUTSTANDING_LOSS',
      tableAlias: 'event_incurred_as_of',
      controls: {
        operator: this.fg.controls.eventOutstandingIndemnityOperator,
        rangeOne: this.fg.controls.eventOutstandingIndemnityRangeOne,
        rangeTwo: this.fg.controls.eventOutstandingIndemnityRangeTwo,
        excludeZero: this.fg.controls.eventOutstandingIndemnityExcludeZero,
      },
    },
    {
      label: 'Medical',
      groupHead: false,
      column: 'OUTSTANDING_MEDICAL',
      tableAlias: 'event_incurred_as_of',
      controls: {
        operator: this.fg.controls.eventOutstandingMedicalOperator,
        rangeOne: this.fg.controls.eventOutstandingMedicalRangeOne,
        rangeTwo: this.fg.controls.eventOutstandingMedicalRangeTwo,
        excludeZero: this.fg.controls.eventOutstandingMedicalExcludeZero,
      },
    },
    {
      label: 'Expense',
      groupHead: false,
      column: 'OUTSTANDING_EXPENSE',
      tableAlias: 'event_incurred_as_of',
      controls: {
        operator: this.fg.controls.eventOutstandingExpenseOperator,
        rangeOne: this.fg.controls.eventOutstandingExpenseRangeOne,
        rangeTwo: this.fg.controls.eventOutstandingExpenseRangeTwo,
        excludeZero: this.fg.controls.eventOutstandingExpenseExcludeZero,
      },
    },
    // Paid bucket
    {
      label: 'Paid',
      groupHead: true,
      column: 'PAID_TOTAL',
      tableAlias: 'event_paid_as_of',
      controls: {
        operator: this.fg.controls.eventPaidTotalOperator,
        rangeOne: this.fg.controls.eventPaidTotalRangeOne,
        rangeTwo: this.fg.controls.eventPaidTotalRangeTwo,
        excludeZero: this.fg.controls.eventPaidTotalExcludeZero,
      },
    },
    {
      label: 'Indemnity',
      groupHead: false,
      column: 'PAID_LOSS',
      tableAlias: 'event_paid_as_of',
      controls: {
        operator: this.fg.controls.eventPaidIndemnityOperator,
        rangeOne: this.fg.controls.eventPaidIndemnityRangeOne,
        rangeTwo: this.fg.controls.eventPaidIndemnityRangeTwo,
        excludeZero: this.fg.controls.eventPaidIndemnityExcludeZero,
      },
    },
    {
      label: 'Medical',
      groupHead: false,
      column: 'PAID_MEDICAL',
      tableAlias: 'event_paid_as_of',
      controls: {
        operator: this.fg.controls.eventPaidMedicalOperator,
        rangeOne: this.fg.controls.eventPaidMedicalRangeOne,
        rangeTwo: this.fg.controls.eventPaidMedicalRangeTwo,
        excludeZero: this.fg.controls.eventPaidMedicalExcludeZero,
      },
    },
    {
      label: 'Expense',
      groupHead: false,
      column: 'PAID_EXPENSE',
      tableAlias: 'event_paid_as_of',
      controls: {
        operator: this.fg.controls.eventPaidExpenseOperator,
        rangeOne: this.fg.controls.eventPaidExpenseRangeOne,
        rangeTwo: this.fg.controls.eventPaidExpenseRangeTwo,
        excludeZero: this.fg.controls.eventPaidExpenseExcludeZero,
      },
    },
    // Recovered bucket
    {
      label: 'Recoveries',
      groupHead: true,
      column: 'RECOVERED_TOTAL',
      tableAlias: 'event_paid_as_of',
      controls: {
        operator: this.fg.controls.eventRecoveredTotalOperator,
        rangeOne: this.fg.controls.eventRecoveredTotalRangeOne,
        rangeTwo: this.fg.controls.eventRecoveredTotalRangeTwo,
        excludeZero: this.fg.controls.eventRecoveredTotalExcludeZero,
      },
    },
    {
      label: 'Indemnity',
      groupHead: false,
      column: 'RECOVERED_LOSS',
      tableAlias: 'event_paid_as_of',
      controls: {
        operator: this.fg.controls.eventRecoveredIndemnityOperator,
        rangeOne: this.fg.controls.eventRecoveredIndemnityRangeOne,
        rangeTwo: this.fg.controls.eventRecoveredIndemnityRangeTwo,
        excludeZero: this.fg.controls.eventRecoveredIndemnityExcludeZero,
      },
    },
    {
      label: 'Medical',
      groupHead: false,
      column: 'RECOVERED_MEDICAL',
      tableAlias: 'event_paid_as_of',
      controls: {
        operator: this.fg.controls.eventRecoveredMedicalOperator,
        rangeOne: this.fg.controls.eventRecoveredMedicalRangeOne,
        rangeTwo: this.fg.controls.eventRecoveredMedicalRangeTwo,
        excludeZero: this.fg.controls.eventRecoveredMedicalExcludeZero,
      },
    },
    {
      label: 'Expense',
      groupHead: false,
      column: 'RECOVERED_EXPENSE',
      tableAlias: 'event_paid_as_of',
      controls: {
        operator: this.fg.controls.eventRecoveredExpenseOperator,
        rangeOne: this.fg.controls.eventRecoveredExpenseRangeOne,
        rangeTwo: this.fg.controls.eventRecoveredExpenseRangeTwo,
        excludeZero: this.fg.controls.eventRecoveredExpenseExcludeZero,
      },
    },
  ];

  setFinancialFromCriteria(criteria: FilterCriteria[]) {
    [...this.claimFinancialCriterias, ...this.eventFinancialCriterias].forEach(
      (financial) => {
        const found = criteria.find(
          (c) =>
            c.tableAlias === financial.tableAlias &&
            (c.column === financial.column ||
              c.column === `NET_${financial.column}`),
        );

        if (!found) return;

        const op = found.operator;
        const value = found.values?.[0] || '';

        if (found.column.startsWith('NET_')) {
          this.selectMode.setValue('net');
        } else {
          this.selectMode.setValue('gross');
        }

        if (op === 'BETWEEN') {
          const [rangeOne, rangeTwo] = value.split('|');
          financial.controls.operator.setValue('between');
          financial.controls.rangeOne.setValue(Number(rangeOne));
          financial.controls.rangeTwo.setValue(Number(rangeTwo));
          financial.controls.excludeZero.setValue(false);
        } else if (op === 'NOTEQUALTO') {
          financial.controls.excludeZero.setValue(true);
        } else if (op === 'GREATERTHANEQUALTO') {
          financial.controls.operator.setValue('greater_than_equal_to');
          financial.controls.rangeOne.setValue(Number(value));
          financial.controls.excludeZero.setValue(false);
        } else if (op === 'GREATERTHAN') {
          financial.controls.operator.setValue('greater_than');
          financial.controls.rangeOne.setValue(Number(value));
          financial.controls.excludeZero.setValue(false);
        } else if (op === 'EQUALS') {
          financial.controls.operator.setValue('equal');
          financial.controls.rangeOne.setValue(Number(value));
          financial.controls.excludeZero.setValue(false);
        } else if (op === 'LESSTHANEQUALTO') {
          financial.controls.operator.setValue('less_than_equal_to');
          financial.controls.rangeOne.setValue(Number(value));
          financial.controls.excludeZero.setValue(false);
        } else if (op === 'LESSTHAN') {
          financial.controls.operator.setValue('less_than');
          financial.controls.rangeOne.setValue(Number(value));
          financial.controls.excludeZero.setValue(false);
        }
      },
    );
  }

  ngOnInit(): void {
    this.section.criteria = this;
    [...this.claimFinancialCriterias, ...this.eventFinancialCriterias].forEach(
      (item) => {
        this.dispose.subs = item.controls.excludeZero.valueChanges.subscribe(
          (checked) => {
            if (checked) {
              item.controls.operator.disable();
              item.controls.rangeOne.disable();
              item.controls.rangeTwo.disable();
              item.controls.operator.setValue('');
              item.controls.rangeOne.setValue(null);
              item.controls.rangeTwo.setValue(null);
            } else {
              item.controls.operator.enable();
              item.controls.rangeOne.enable();
              item.controls.rangeTwo.enable();
            }
            this.emitCount();
          },
        );

        this.dispose.subs = item.controls.operator.valueChanges.subscribe(
          (operator) => {
            this.updateValidation(item, operator);
            this.emitCount();
          },
        );

        this.dispose.subs = item.controls.rangeOne.valueChanges.subscribe(
          () => {
            item.controls.rangeTwo.updateValueAndValidity({ emitEvent: false });
            this.emitCount();
          },
        );
        this.dispose.subs = item.controls.rangeTwo.valueChanges.subscribe(
          () => {
            item.controls.rangeOne.updateValueAndValidity({ emitEvent: false });
            this.emitCount();
          },
        );
      },
    );

    if (this.section.values?.length) {
      this.setFinancialFromCriteria(this.section.values);
    }
  }

  private updateValidation(item: FinancialCriteria, operator: string): void {
    item.controls.rangeOne.clearValidators();
    item.controls.rangeTwo.clearValidators();

    if (operator) {
      item.controls.rangeOne.addValidators([Validators.required]);
      item.controls.rangeOne.markAsTouched();

      if (operator === 'between') {
        item.controls.rangeOne.addValidators([
          ValidatorsEx.currencyLessThan(
            item.controls.rangeTwo,
            false,
            'From value must be less than To value',
          ),
        ]);
        item.controls.rangeTwo.addValidators([
          Validators.required,
          ValidatorsEx.currencyGreaterThan(
            item.controls.rangeOne,
            false,
            'To value must be greater than From value',
          ),
        ]);
        item.controls.rangeTwo.markAsTouched();
      }
    }

    item.controls.rangeOne.updateValueAndValidity({ emitEvent: false });
    item.controls.rangeTwo.updateValueAndValidity({ emitEvent: false });
  }

  private isComplete(criteria: FinancialCriteria): boolean {
    if (criteria.controls.excludeZero.value) {
      return true;
    }
    const operator = criteria.controls.operator.value;
    if (!operator) {
      return false;
    }
    if (operator === 'between') {
      return (
        criteria.controls.rangeOne.value !== null &&
        criteria.controls.rangeTwo.value !== null
      );
    }
    return criteria.controls.rangeOne.value !== null;
  }

  private emitCount(): void {
    this.section.count.set(
      [...this.claimFinancialCriterias, ...this.eventFinancialCriterias].filter(
        (c) => this.isComplete(c),
      ).length,
    );
  }

  valid(): boolean {
    return this.fg.valid;
  }

  getCriteria(): FilterCriteria[] {
    const isNet = this.selectMode.value === 'net';

    return [...this.claimFinancialCriterias, ...this.eventFinancialCriterias]
      .filter((c) => this.isComplete(c))
      .map((c) => {
        const fieldName = isNet ? `NET_${c.column}` : c.column;

        if (c.controls.excludeZero.value) {
          return {
            tableAlias: c.tableAlias,
            column: fieldName,
            displayName: '',
            operator: 'NOTEQUALTO',
            values: ['0'],
            lineOfBusiness: '',
            dataType: 'NUMBER',
            isClaimLab: false,
            formattedOperator: '',
            formattedValue: '',
            filterSection: 'Financial',
            filterSubsection: '',
          } as FilterCriteria;
        }

        const op = c.controls.operator.value;
        const operatorMap: Record<string, string> = {
          greater_than_equal_to: 'GREATERTHANEQUALTO',
          greater_than: 'GREATERTHAN',
          equal: 'EQUALS',
          between: 'BETWEEN',
          less_than_equal_to: 'LESSTHANEQUALTO',
          less_than: 'LESSTHAN',
        };

        const values =
          op === 'between'
            ? [`${c.controls.rangeOne.value}|${c.controls.rangeTwo.value}`]
            : [`${c.controls.rangeOne.value}`];

        return {
          tableAlias: c.tableAlias,
          column: fieldName,
          displayName: '',
          operator: operatorMap[op] || op.toUpperCase(),
          values,
          lineOfBusiness: '',
          dataType: 'NUMBER',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Financial',
          filterSubsection: '',
        } as FilterCriteria;
      });
  }
}

interface FinancialCriteria {
  label: string;
  groupHead: boolean;
  column: string;
  tableAlias: string;
  controls: {
    operator: FormControl<string>;
    rangeOne: FormControl<number | null>;
    rangeTwo: FormControl<number | null>;
    excludeZero: FormControl<boolean>;
  };
}
